import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ChevronRight, ArrowRight, Calendar } from "lucide-react";
import { API, scrollToTop, setCanonical } from "../lib/config";
import { Nav, Footer, HeroBanner, CookieConsent, QuizTeaser } from "../components/layout";
import { ChatBot } from "../components/chat";
import { AffiliateBanner, AdSensePlaceholder, MoneyStrip } from "../components/ads";

export function BlogList() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    setCanonical("/blog");
    document.title = "Auto Insurance Blog | QuotesByCity";
    axios.get(`${API}/blog/posts?limit=50`).then(r => setPosts(r.data.posts || [])).catch(() => {});
  }, []);

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <Nav />
      <HeroBanner title="Auto Insurance Blog" subtitle="Tips, guides, and myth-busting articles to help you make smarter insurance decisions" badge="Educational Articles" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* MAIN CONTENT */}
          <div className="flex-1 min-w-0 space-y-4">
            {posts.length === 0 && <p className="text-center text-slate-400 py-10">No articles yet. Check back soon!</p>}
            {posts.map((p, i) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} onClick={scrollToTop} className="block bg-white rounded-xl border border-slate-200 p-5 hover:border-sky-300 hover:shadow-sm transition-all group" data-testid={`blog-item-${i}`}>
                <h2 className="font-bold text-slate-900 group-hover:text-sky-700 transition-colors mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.title}</h2>
                <p className="text-sm text-slate-500 line-clamp-2 mb-2">{p.excerpt}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400 flex items-center gap-1"><Calendar size={12} /> {p.created_at?.split("T")[0]}</span>
                  <span className="text-xs text-sky-700 font-medium flex items-center gap-1">Read more <ChevronRight size={12} /></span>
                </div>
              </Link>
            ))}

            {/* MOBILE ADS */}
            <div className="lg:hidden">
              <MoneyStrip />
            </div>
          </div>

          {/* SIDEBAR - Desktop */}
          <aside className="w-full lg:w-72 shrink-0 space-y-6 hidden lg:block" data-testid="sidebar">
            <div className="lg:sticky lg:top-20 space-y-6">
              <AffiliateBanner />
              <AdSensePlaceholder size="large" />
            </div>
          </aside>
        </div>
      </div>
      <Footer />
      <ChatBot />
      <CookieConsent />
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);

  useEffect(() => {
    axios.get(`${API}/blog/posts/${slug}`).then(r => {
      const p = r.data;
      setPost(p);
      setCanonical(`/blog/${slug}`);
      document.title = `${p.title} | QuotesByCity Blog`;
      const schemas = [
        ["blog-article", { "@context": "https://schema.org", "@type": "Article", "headline": p.title, "description": p.excerpt, "datePublished": p.created_at, "author": { "@type": "Organization", "name": "QuotesByCity" }, "publisher": { "@type": "Organization", "name": "QuotesByCity", "url": "https://www.quotesbycity.com" } }],
        ["blog-speak", { "@context": "https://schema.org", "@type": "WebPage", "name": p.title, "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", "[data-testid='blog-content']"] }, "url": `https://www.quotesbycity.com/blog/${slug}` }],
      ];
      schemas.forEach(([id, schema]) => {
        let el = document.getElementById(`schema-${id}`);
        if (!el) { el = document.createElement("script"); el.id = `schema-${id}`; el.type = "application/ld+json"; document.head.appendChild(el); }
        el.textContent = JSON.stringify(schema);
      });
    }).catch(() => {});
    return () => { ["blog-article", "blog-speak"].forEach(id => { const el = document.getElementById(`schema-${id}`); if (el) el.remove(); }); };
  }, [slug]);

  if (!post) return <div className="min-h-screen bg-[#FAFAFA]"><Nav /><div className="max-w-4xl mx-auto px-4 py-20 text-center text-slate-400">Loading...</div></div>;

  const renderMarkdown = (md) => {
    if (!md) return "";
    return md
      .replace(/^### (.*$)/gm, '<h3 class="text-lg font-bold text-slate-900 mt-6 mb-2" style="font-family:Outfit,sans-serif">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold text-slate-900 mt-8 mb-3" style="font-family:Outfit,sans-serif">$1</h2>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-slate-900">$1</strong>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-sky-700 underline hover:text-sky-800" target="_blank" rel="noopener noreferrer">$1</a>')
      .replace(/^- (.*$)/gm, '<li class="ml-4 text-slate-600 text-sm mb-1">$1</li>')
      .replace(/\n\n/g, '</p><p class="text-sm text-slate-600 leading-relaxed mb-3">')
      .replace(/\n/g, '<br/>');
  };

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <Nav />
      <HeroBanner title={post.title} subtitle={post.excerpt} badge="Blog Article" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* MAIN CONTENT */}
          <div className="flex-1 min-w-0">
            <article className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8" data-testid="blog-content">
              <p className="text-xs text-slate-400 mb-6">{post.created_at?.split("T")[0]}</p>
              <div className="prose-sm" dangerouslySetInnerHTML={{ __html: `<p class="text-sm text-slate-600 leading-relaxed mb-3">${renderMarkdown(post.content)}</p>` }} />
            </article>

            <div className="mt-8 space-y-6">
              {/* MOBILE ADS */}
              <div className="lg:hidden">
                <MoneyStrip />
              </div>

              <QuizTeaser />
              <div className="text-center">
                <Link to="/blog" onClick={scrollToTop} className="text-sky-700 font-medium text-sm inline-flex items-center gap-1"><ArrowRight size={14} className="rotate-180" /> Back to All Articles</Link>
              </div>
            </div>
          </div>

          {/* SIDEBAR - Desktop */}
          <aside className="w-full lg:w-72 shrink-0 space-y-6 hidden lg:block" data-testid="sidebar">
            <div className="lg:sticky lg:top-20 space-y-6">
              <AffiliateBanner />
              <AdSensePlaceholder size="large" />
            </div>
          </aside>
        </div>
      </div>
      <Footer />
      <ChatBot />
      <CookieConsent />
    </div>
  );
}
